import { trackError } from '@iheartjane/tracking';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type { CustomerThunkAction } from '@jane/monolith/customer/customerAction';
import { CartSource } from '@jane/monolith/sources/cart';
import type { BundlePossibilities } from '@jane/shared/types';

export const getBundlePossibilities =
  (cartUuid: string): CustomerThunkAction =>
  (dispatch, getState) => {
    const {
      bundlePossibilities: { touchedProductId },
    } = getState();

    if (!(touchedProductId && cartUuid)) {
      return;
    }

    return CartSource.getBundlePossibilities(cartUuid, touchedProductId)
      .then((result: BundlePossibilities) =>
        dispatch(getBundlePossibilitiesSuccess(result))
      )
      .catch((error) => {
        trackError(new Error('Bundle possibilities fetch failed'), {
          error,
        });
      });
  };

export const defaultBundlePossibilities =
  (): BundlePossibilitiesState['bundlePossibilities'] => ({
    status: null,
    special_id: null,
    discountable_product_ids: [],
  });

export type BundlePossibilitiesState = {
  bundlePossibilities: BundlePossibilities;
  filterDiscountableProducts: boolean;
  showingBundlePossibilities: boolean;
  touchedProductId?: string | number;
};

const getInitialState = (): BundlePossibilitiesState => ({
  bundlePossibilities: defaultBundlePossibilities(),
  filterDiscountableProducts: false,
  showingBundlePossibilities: false,
  touchedProductId: undefined,
});

const bundlePossibilitiesSlice = createSlice({
  name: 'bundlePossibilities',
  initialState: getInitialState(),
  reducers: {
    showBundlePossibilities: (state) => {
      return { ...state, showingBundlePossibilities: true };
    },
    stopShowBundlePossibilities: () => {
      return getInitialState();
    },
    applyDiscountableProductsFilter: (state) => {
      return { ...state, filterDiscountableProducts: true };
    },
    getBundlePossibilitiesSuccess: (
      state,
      action: PayloadAction<BundlePossibilities>
    ) => {
      return { ...state, bundlePossibilities: action.payload };
    },
    setTouchedProduct: (
      state,
      action: PayloadAction<string | number | undefined>
    ) => {
      return {
        ...state,
        bundlePossibilities: defaultBundlePossibilities(),
        touchedProductId: action.payload,
      };
    },
    clearTouchedProduct: (state) => {
      return {
        ...state,
        touchedProductId: undefined,
      };
    },
  },
  selectors: {
    selectBundlePossibilities: (state) => state,
  },
});

export const {
  reducer: bundlePossibilitiesReducer,
  actions: {
    showBundlePossibilities,
    stopShowBundlePossibilities,
    applyDiscountableProductsFilter,
    getBundlePossibilitiesSuccess,
    setTouchedProduct,
    clearTouchedProduct,
  },
  selectors: { selectBundlePossibilities },
} = bundlePossibilitiesSlice;

export type BundlePossibilitiesActions =
  | ReturnType<typeof applyDiscountableProductsFilter>
  | ReturnType<typeof clearTouchedProduct>
  | ReturnType<typeof getBundlePossibilitiesSuccess>
  | ReturnType<typeof setTouchedProduct>
  | ReturnType<typeof showBundlePossibilities>
  | ReturnType<typeof stopShowBundlePossibilities>;
